import Logo from '../../assets/logo-same-color.svg?react';
import { Form, Formik, FormikConfig } from 'formik';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import * as yup from 'yup';
import { Button } from '../../components/Button';
import { FormikCheckBox } from '../../components/formik/FormCheckBox';
import { FormikInput } from '../../components/formik/FormInput';
import { useLogin } from '../../hooks/modules/user/useLogin';
import { useValidationTranslations } from '../../hooks/useValidationTranslations';
import { T, useT } from '../../translation/src';
import { VerificationScreen } from './VerificationCodeForm';
import { LOGIN_REDIRECT_PARAM, safeRedirectTarget } from '../../utils/loginRedirect';

const useValidationSchema = () => {
  const translations = useValidationTranslations();

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup.string().trim().email(translations.validEmail).required(translations.isRequired),
        password: yup.string().trim().required(translations.isRequired),
      }),
    [translations],
  );

  return schema;
};

interface FormValues {
  email: string;
  password: string;
  remember: boolean;
}

const useTranslations = () => ({
  wrongCombination: useT('wrong email/password combination', { swc: true }),
  email: useT('email', { swc: true }),
  password: useT('password', { swc: true }),
  rememberMe: useT('remember me', { swc: true }),
});

const Login_: React.FC2 = () => {
  const translations = useTranslations();
  const navigate = useNavigate();
  const validationSchema = useValidationSchema();
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Credentials of the last attempt refused for a missing verification: the code entry takes over the form. */
  const [pendingVerification, setPendingVerification] = useState<{ email: string; password: string } | null>(null);
  const login = useLogin();
  const [searchParams] = useSearchParams();
  // Where to go after signing in: the page that sent the visitor here, or home.
  const redirectTarget = safeRedirectTarget(searchParams.get(LOGIN_REDIRECT_PARAM));
  const [, setLastSelectedOrganisation] = useLocalStorage('lastSelectedOrganisation', '');

  useEffect(() => {
    setLastSelectedOrganisation('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      try {
        setIsSubmitting(true);
        const response = await login(values);
        if (response.status === 200) return navigate(redirectTarget, { replace: true });

        if (response.status === 403) {
          const body = (await response.json().catch(() => ({}))) as { message?: string };
          if (body.message === 'EMAIL_NOT_VERIFIED') {
            setPendingVerification({ email: values.email.trim().toLowerCase(), password: values.password });
            return;
          }
        }
        if (response.status === 401) throw new Error();
      } catch {
        helpers.setFieldError('password', translations.wrongCombination);
      } finally {
        setIsSubmitting(false);
      }
    },
    [login, navigate, redirectTarget, translations.wrongCombination],
  );

  const initialValues = useMemo(() => ({ email: '', password: '', remember: true }), []);

  if (pendingVerification) {
    return (
      // The code mailed at sign-up may be long gone: no wait before the first resend.
      <VerificationScreen
        email={pendingVerification.email}
        password={pendingVerification.password}
        initialWaitSeconds={0}
        redirectTo={redirectTarget}
        onEmailChanged={(email) => setPendingVerification({ ...pendingVerification, email })}
        before={<T _str="Your account is not activated yet. We emailed a 6-digit code to" />}
        after={<T _str="Enter it below, or request a new one." />}
        footer={
          <p className="text-sm text-center">
            <button type="button" className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-400 dark:hover:text-neon-green-300" onClick={() => setPendingVerification(null)}>
              <T _str="use a different account" swc />
            </button>
          </p>
        }
      />
    );
  }

  return (
    <div className="flex items-center justify-center w-full min-h-full px-4 py-12 sm:px-6 lg:px-8 dark:bg-[#313131] dark:text-white">
      <div className="w-full max-w-md space-y-8">
        <div>
          <Logo className="w-auto mx-auto text-eerie-black dark:text-white" />
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-center">
            <T _str="Sign in to your account" />
          </h2>
          <p className="mt-2 text-sm text-center">
            <T _str="Or" />{' '}
            <Link className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-400 dark:hover:text-neon-green-300" to={`/register`}>
              <T _str="get started - free" swc />
            </Link>
          </p>
        </div>
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          <Form className="mt-8">
            <FormikInput name="email" type="email" autoComplete="email" label={translations.email} placeholder={translations.email} />
            <FormikInput name="password" type="password" autoComplete="current-password" label={translations.password} placeholder={translations.password} />

            <div className="flex items-center justify-between">
              <FormikCheckBox name="remember" label={translations.rememberMe} />
              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-400 dark:hover:text-neon-green-300">
                  <T _str="Forgot your password?" />
                </Link>
              </div>
            </div>
            <div>
              <Button variant="primary" type="submit" className="items-center justify-center w-full" loading={isSubmitting}>
                <T _str="Sign in" />
              </Button>
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export const Login = memo(Login_);
